import { useTheme, alpha } from '@mui/material';

import { ChartConfig, ChartType } from 'types/charts';

import { useComputeSpendControls } from './useComputeSpendControls';
import { useCostOverTime } from '../../../../cost-report/hooks/useCostOverTime';
import { useCostReportContext } from '../../../../cost-report/hooks/useCostReportContext';
import { CostReportChartType } from '../../../../cost-report/types/costOverTime';

const minBarSize = 16;
const maxBarSize = 20;

export const useComputeSpendChart = (showStorage: boolean): ChartConfig[] => {
  const theme = useTheme();
  const { chartType } = useCostReportContext();
  const { chartNodeType } = useComputeSpendControls();
  const { projectEndOfDayCost } = useCostOverTime();

  if (chartType === CostReportChartType.AREA) {
    return [
      {
        type: ChartType.AREA,
        enabled: chartNodeType.spot,
        config: {
          dataKey: 'spot',
          fill: theme.palette.indigo[800]!,
        },
        stackId: 'a',
      },
      {
        type: ChartType.AREA,
        enabled: chartNodeType.fallback,
        config: {
          dataKey: 'fallback',
          fill: theme.palette.yellow[200]!,
        },
        stackId: 'a',
      },
      {
        type: ChartType.AREA,
        enabled: chartNodeType.onDemand,
        config: { dataKey: 'onDemand', fill: theme.palette.blue[300]! },
        stackId: 'a',
      },
      {
        type: ChartType.AREA,
        enabled: chartNodeType.storage && showStorage,
        config: {
          dataKey: 'storage',
          fill: theme.palette.aqua[300]!,
        },
        stackId: 'a',
      },
    ];
  }

  const barDefaults = { barSize: minBarSize, maxBarSize };

  return [
    {
      type: ChartType.BAR,
      enabled: chartNodeType.spot,
      config: {
        ...barDefaults,
        dataKey: 'spot',
        fill: theme.palette.indigo[800]!,
      },
      stackId: 'a',
    },
    {
      type: ChartType.BAR,
      enabled: projectEndOfDayCost && chartNodeType.spot,
      config: {
        ...barDefaults,
        dataKey: 'spotForecastDelta',
        fill: 'none',
        stroke: alpha(theme.palette.indigo[800]!, 0.4),
      },
      stackId: 'a',
    },
    {
      type: ChartType.BAR,
      enabled: chartNodeType.fallback,
      config: {
        ...barDefaults,
        dataKey: 'fallback',
        stroke: theme.palette.yellow[200]!,
      },
      stackId: 'a',
    },
    {
      type: ChartType.BAR,
      enabled: projectEndOfDayCost && chartNodeType.fallback,
      config: {
        ...barDefaults,
        dataKey: 'fallbackForecastDelta',
        fill: 'none',
        stroke: alpha(theme.palette.yellow[200]!, 0.7),
      },
      stackId: 'a',
    },
    {
      type: ChartType.BAR,
      enabled: chartNodeType.onDemand,
      config: {
        ...barDefaults,
        dataKey: 'onDemand',
        fill: theme.palette.blue[300]!,
      },
      stackId: 'a',
    },
    {
      type: ChartType.BAR,
      enabled: projectEndOfDayCost && chartNodeType.onDemand,
      config: {
        ...barDefaults,
        dataKey: 'onDemandForecastDelta',
        fill: 'none',
        stroke: alpha(theme.palette.blue[300]!, 0.6),
      },
      stackId: 'a',
    },
    {
      type: ChartType.BAR,
      enabled: chartNodeType.storage && showStorage,
      config: {
        ...barDefaults,
        dataKey: 'storage',
        fill: theme.palette.aqua[300]!,
      },
      stackId: 'a',
    },
    {
      type: ChartType.BAR,
      enabled: projectEndOfDayCost && chartNodeType.storage && showStorage,
      config: {
        ...barDefaults,
        dataKey: 'storageForecastDelta',
        fill: 'none',
        stroke: alpha(theme.palette.aqua[300]!, 0.6),
      },
      stackId: 'a',
    },
  ];
};
